import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import {
  CalendarIcon,
  Check,
  ChevronDown,
  ChevronRight,
  CircleArrowLeft,
  Download,
  Loader2,
  Search,
  Send,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  type Enrollment,
  type ProcessingRecord,
  type SessionBillingBreakdownResponse,
  type SessionDetail,
  type StudentBillingData,
  type StudentInfo,
  useGetSessionBillingBreakdown,
} from '~entities/payment/api/hourly-tuition/get-session-billing-breakdown';
import { useGetSessionBillingBreakdownV2 } from '~entities/payment/api/hourly-tuition/get-session-billing-breakdown-v2';
import {
  HourlyTuitionDiscountType,
  HourlyTuitionPostingStatus,
  HourlyTuitionPostStudentStatus,
  isHourlyTuitionPostSkipStatus,
} from '~entities/payment/api/hourly-tuition/hourly-tuition.constants';
import { usePostTuition } from '~entities/payment/api/hourly-tuition/post-to-accounts';
import { useSaveDraft } from '~entities/payment/api/hourly-tuition/save-draft';
import { useUpdateDraft } from '~entities/payment/api/hourly-tuition/update-draft';
import { useGetAllSessions } from '~entities/session/api/get-sessions';
import { useGetV2ModeStatus } from '~entities/v2-mode/api/v2-mode-api';

import {
  formatHourlyTuitionAmount,
  getInvoiceStatusBadge,
  getStudentChargeStatusBadge,
  getWeekChargeStatusBadge,
  getWeekInvoiceStatusBadge,
  isHourlySessionOutOfRange,
  isHourlyWeekOutOfRange,
  isStudentFullyCharged,
  isWeekChargeStatusVisible,
  resolveHourlySessionApplicableRange,
  resolvePostedOrChargedLabel,
  resolveStudentInvoiceStatus,
} from '~features/payment/utils/hourly-tuition-display-status';
import {
  buildHourlyPostFailureStorageKey,
  mergeHourlyPostFailures,
  readHourlyPostFailures,
  writeHourlyPostFailures,
} from '~features/payment/utils/hourly-tuition-post-failure-storage';
import { resolveStaffDiscountForPost } from '~features/payment/utils/resolve-staff-discount-for-post';
import { SessionSelector } from '~features/shared/session-selector';

import { useGetCurrency } from '~shared/api/currency-api';
import { DateTimePicker } from '~shared/components/date-time-picker';
import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { TruncatedPagination } from '~shared/components/truncated-pagination';
import { WarningDialog } from '~shared/components/warning-dialog';
import { Calendar } from '~shared/shadcn/calendar';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import { Switch } from '~shared/shadcn/ui/switch';

interface WeeklyBreakdownItem {
  weekStart: string;
  weekEnd: string;
  minutes: number;
  hourlyRate: number;
  billingAmount: number;
  applicableRange: {
    minMinute: number;
    maxMinute: number;
    flatRate: number;
  } | null;
  errorMessage: string | null;
  isProrated?: boolean;
  days?: Array<string>;
  prorationReason?: string | null;
  isPosted?: boolean;
  postedInvoiceId?: string | null;
  postedInvoiceStatus?: string | null;
  postedDateRange?: { fromDate: string; toDate: string } | null;
}

interface TuitionSession {
  id: string;
  enrollmentId?: string;
  student: string;
  days: string; // Comma-separated days like "Mon, Wed"
  class_type: string;
  duration_minutes: number;
  total_minutes: number;
  /** Per-enrollment display data (so multi-enrollment students show correct range/rate per row) */
  applicableRange?: {
    minMinute: number;
    maxMinute: number;
    flatRate: number;
  } | null;
  totalTuition?: number;
  discountApplied?: number;
  weeklyBreakdown?: Array<WeeklyBreakdownItem>;
}

interface StudentGroup {
  student: string;
  studentId: string;
  sessions: Array<TuitionSession>;
  total_minutes: number;
  base_rate: number;
  discount_type: HourlyTuitionDiscountType;
  discount_value: number;
  discount_applied: number;
  /** V2 auto MSD already included in preview final_amount; keep when editing staff $. */
  multi_student_discount: number;
  final_amount: number;
  isExpanded: boolean;
  isDone: boolean;
  isDraft: boolean; // Flag indicating if this student has a draft
  draftId?: string; // ID of the draft if it exists
  isAlreadyProcessed?: boolean; // All billable weeks in this fetch were posted
  isPartiallyProcessed?: boolean; // Some weeks posted, some still billable
  invoiceId?: string | null; // Invoice ID if previously processed
  invoiceStatus?: string | null; // Invoice status if previously processed
  /** Last post attempt failure (session-local and/or from preview). */
  lastPostFailure?: string | null;
  applicableRange?: {
    minMinute: number;
    maxMinute: number;
    flatRate: number;
  } | null;
  errorMessage?: string | null;
  billingRuleName?: string;
  weeklyBreakdown?: Array<WeeklyBreakdownItem>;
  /** Raw enrollments from API for per-enrollment weekly breakdown and session details */
  enrollments?: Array<{
    enrollmentId: string;
    enrollmentTitle: string;
    billingRuleName: string;
    sessionDetails: Array<{ day: string; class: string; minutes: number }>;
    totalMinutes: number;
    totalTuition: number;
    discountApplied: number;
    applicableRange: {
      minMinute: number;
      maxMinute: number;
      flatRate: number;
    } | null;
    weeklyBreakdown?: Array<WeeklyBreakdownItem>;
  }>;
}
const tuitionSessionColumnHelper = createColumnHelper<TuitionSession>();

// Utility function to format dates for API without timezone issues
const formatDateForAPI = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isStudentSelectableForPosting = (group: StudentGroup) =>
  !group.isAlreadyProcessed;

const getSessionWeeklyBreakdown = (
  session: TuitionSession,
  group: StudentGroup,
): Array<WeeklyBreakdownItem> | undefined => {
  if (session.weeklyBreakdown?.length) {
    return session.weeklyBreakdown;
  }

  if (session.enrollmentId && group.enrollments?.length) {
    const enrollment = group.enrollments.find(
      (item) => item.enrollmentId === session.enrollmentId,
    );
    if (enrollment?.weeklyBreakdown?.length) {
      return enrollment.weeklyBreakdown;
    }
  }

  if (group.sessions.length === 1 && group.weeklyBreakdown?.length) {
    return group.weeklyBreakdown;
  }

  return undefined;
};

const getSessionWeeklyBreakdownTitle = (
  session: TuitionSession,
  group: StudentGroup,
): string | undefined => {
  if (session.enrollmentId && group.enrollments?.length) {
    return group.enrollments.find(
      (item) => item.enrollmentId === session.enrollmentId,
    )?.enrollmentTitle;
  }

  return session.class_type;
};

const getSessionBillingRuleName = (
  session: TuitionSession,
  group: StudentGroup,
): string | undefined => {
  if (session.enrollmentId && group.enrollments?.length) {
    return group.enrollments.find(
      (item) => item.enrollmentId === session.enrollmentId,
    )?.billingRuleName;
  }

  return group.billingRuleName;
};

function WeeklyBreakdownPanel({
  weeklyBreakdown,
  title,
  billingRuleName,
  currencySymbol,
}: {
  weeklyBreakdown: Array<WeeklyBreakdownItem>;
  title?: string;
  billingRuleName?: string;
  currencySymbol: string;
}) {
  return (
    <div className='border-t border-[#E5E7EB] bg-[#F9FAFB] p-4'>
      <h5 className='mb-3 text-sm font-medium text-[#111827]'>
        Weekly Billing Breakdown
      </h5>
      {title ? (
        <h6 className='mb-2 text-xs font-medium uppercase tracking-wider text-[#6B7280]'>
          {title}
        </h6>
      ) : null}
      <div className='space-y-3'>
        {weeklyBreakdown.map((week, weekIndex) => {
          const showWeekChargeStatus = isWeekChargeStatusVisible(week);

          return (
            <div
              key={weekIndex}
              className={`rounded-lg border p-4 ${
                showWeekChargeStatus
                  ? 'border-orange-200 bg-orange-50/40'
                  : 'border-[#E5E7EB] bg-white'
              }`}
            >
              <div className='mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
                <div className='flex items-center gap-2'>
                  <h6 className='text-sm font-medium text-[#111827]'>
                    Week {weekIndex + 1}
                  </h6>
                  {getWeekChargeStatusBadge(week)}
                  {getWeekInvoiceStatusBadge(week)}
                </div>
                <span
                  className={`text-sm font-medium ${
                    showWeekChargeStatus ? 'text-orange-700' : 'text-[#059669]'
                  }`}
                >
                  {currencySymbol}
                  {formatHourlyTuitionAmount(week.billingAmount)}
                </span>
              </div>
              <div className='grid grid-cols-1 gap-2 text-xs text-[#6B7280] md:grid-cols-4'>
                <div>
                  <span className='font-medium'>Period:</span>
                  <div className='mt-1'>
                    {new Date(week.weekStart).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      timeZone: 'UTC',
                    })}{' '}
                    -{' '}
                    {new Date(week.weekEnd).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      timeZone: 'UTC',
                    })}
                  </div>
                </div>
                <div>
                  <span className='font-medium'>Minutes:</span>
                  <div className='mt-1'>{week.minutes} min</div>
                </div>
                <div>
                  <span className='font-medium'>Rate:</span>
                  <div className='mt-1'>
                    {currencySymbol}
                    {formatHourlyTuitionAmount(week.hourlyRate)}
                  </div>
                </div>
                <div>
                  <span className='font-medium'>Range:</span>
                  <div className='mt-1'>
                    {week.applicableRange ? (
                      <span className='rounded bg-[#FEF3C7] px-2 py-1 text-xs font-medium text-[#92400E]'>
                        {week.applicableRange.minMinute} -{' '}
                        {week.applicableRange.maxMinute} min
                      </span>
                    ) : isHourlyWeekOutOfRange(week) ? (
                      <span className='rounded bg-[#FEE2E2] px-2 py-1 text-xs font-medium text-[#DC2626]'>
                        Out of Range
                      </span>
                    ) : (
                      <span className='text-xs text-[#6B7280]'>-</span>
                    )}
                  </div>
                </div>
              </div>
              {week.errorMessage ? (
                <div className='mt-3 rounded bg-[#FEF2F2] p-2 text-xs text-[#DC2626]'>
                  {week.errorMessage}
                </div>
              ) : null}
              {week.days && week.days.length > 0 ? (
                <div className='mt-3'>
                  <span className='text-xs font-medium text-[#6B7280]'>
                    Days:
                  </span>
                  <div className='mt-1 flex flex-wrap gap-1'>
                    {week.days.map((day, dayIndex) => (
                      <span
                        key={dayIndex}
                        className='rounded bg-[#F3F4F6] px-2 py-1 text-xs font-medium text-[#374151]'
                      >
                        {day}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {billingRuleName ? (
        <div className='mt-3 text-xs text-[#6B7280]'>
          <span className='font-medium'>Billing Rule:</span> {billingRuleName}
        </div>
      ) : null}
    </div>
  );
}

function TuitionSessionsTable({
  group,
  currencySymbol,
}: {
  group: StudentGroup;
  currencySymbol: string;
}) {
  const columns = useMemo(
    () => [
      tuitionSessionColumnHelper.accessor('days', {
        header: 'Days',
        cell: (info) => <span className='capitalize'>{info.getValue()}</span>,
      }),
      tuitionSessionColumnHelper.accessor('class_type', {
        header: 'Class',
      }),
      tuitionSessionColumnHelper.accessor('duration_minutes', {
        header: 'Minutes per week',
      }),
      tuitionSessionColumnHelper.accessor('total_minutes', {
        header: 'Total minutes',
      }),
      tuitionSessionColumnHelper.display({
        id: 'billingRange',
        header: 'Billing range',
        cell: ({ row }) => {
          const sessionRangeParams = {
            weeklyBreakdown: row.original.weeklyBreakdown,
            sessionRange: row.original.applicableRange,
            groupRange: group.applicableRange,
          };
          const range = resolveHourlySessionApplicableRange(sessionRangeParams);
          return range ? (
            <span className='rounded bg-[#FEF3C7] px-2 py-1 text-xs font-medium text-[#92400E]'>
              {range.minMinute} - {range.maxMinute} min
            </span>
          ) : isHourlySessionOutOfRange(sessionRangeParams) ? (
            <span className='rounded bg-[#FEE2E2] px-2 py-1 text-xs font-medium text-[#DC2626]'>
              Out of Range
            </span>
          ) : (
            '—'
          );
        },
      }),
      tuitionSessionColumnHelper.display({
        id: 'rateApplied',
        header: 'Rate applied',
        cell: ({ row }) => {
          const session = row.original;
          const amount = session.weeklyBreakdown?.length
            ? session.weeklyBreakdown.reduce(
                (sum, week) => sum + week.billingAmount,
                0,
              )
            : typeof session.totalTuition === 'number'
              ? session.totalTuition
              : group.weeklyBreakdown?.length
                ? group.weeklyBreakdown.reduce(
                    (sum, week) => sum + week.billingAmount,
                    0,
                  )
                : (group.applicableRange?.flatRate ?? 0);
          return (
            <span
              className={cn(
                'font-medium',
                amount > 0 ? 'text-[#059669]' : 'text-[#DC2626]',
              )}
            >
              {currencySymbol}
              {formatHourlyTuitionAmount(amount)}
            </span>
          );
        },
      }),
      tuitionSessionColumnHelper.display({
        id: 'discountApplied',
        header: 'Discount applied',
        cell: () => (
          <span className='font-medium text-[#DC2626]'>
            {currencySymbol}
            {group.discount_applied.toFixed(2)}
          </span>
        ),
      }),
      tuitionSessionColumnHelper.display({
        id: 'weeklyDetails',
        header: 'Weekly details',
        cell: ({ row }) => {
          const breakdown = getSessionWeeklyBreakdown(row.original, group);
          if (!breakdown?.length) return '—';
          return (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='text-[#4F46E5]'
              onClick={row.getToggleExpandedHandler()}
            >
              {row.getIsExpanded() ? (
                <ChevronDown className='size-3' />
              ) : (
                <ChevronRight className='size-3' />
              )}
              {row.getIsExpanded() ? 'Hide details' : 'Show details'}
            </Button>
          );
        },
      }),
    ],
    [currencySymbol, group],
  );

  return (
    <EnrollioTable
      totalLabel='Class sessions'
      filtersLayout='inline'
      filterHeader={(table) => (
        <EnrollioTableSearch
          table={table}
          placeholder='Search session details...'
        />
      )}
      data={group.sessions}
      columns={columns}
      getRowId={(row) => row.id}
      getRowCanExpand={(row) =>
        (getSessionWeeklyBreakdown(row.original, group)?.length ?? 0) > 0
      }
      expandedRow={(row) => {
        const breakdown = getSessionWeeklyBreakdown(row.original, group);
        return breakdown ? (
          <WeeklyBreakdownPanel
            weeklyBreakdown={breakdown}
            title={getSessionWeeklyBreakdownTitle(row.original, group)}
            billingRuleName={getSessionBillingRuleName(row.original, group)}
            currencySymbol={currencySymbol}
          />
        ) : null;
      }}
      paginationVariant='compact'
      mobile={false}
      tableClassName='min-w-[900px] text-sm'
      emptyContent='No session details.'
      ariaLabel={`Session details for ${group.student}`}
    />
  );
}

export const ReviewHourlyTuitionPage = () => {
  const { data: currencyDetails } = useGetCurrency();
  const { data: v2ModeStatus, isLoading: isV2ModeLoading } =
    useGetV2ModeStatus();
  /** Same flag as V2 Mode settings + bulk/single class enrollment. */
  const isV2HourlyTuition = v2ModeStatus?.isV2 === true;
  const currencySymbol = currencyDetails?.symbol ?? '$';

  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [studentGroups, setStudentGroups] = useState<Array<StudentGroup>>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyUncharged, setShowOnlyUncharged] = useState(false);
  const [selectedSession, setSelectedSession] = useState<
    | {
        label: string;
        value: string;
      }
    | undefined
    | null
  >(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [isScheduledPayment, setIsScheduledPayment] = useState(false);
  /** V2 post mode: invoice-only is the default safe action (ENR-3102). */
  const [v2PostMode, setV2PostMode] = useState<
    'invoiceOnly' | 'chargeNow' | 'schedule'
  >('invoiceOnly');
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(
    undefined,
  );
  const [confirmationDescription, setConfirmationDescription] = useState('');
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const [processingHistory, setProcessingHistory] =
    useState<Array<ProcessingRecord> | null>(null);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const studentsPerPage = 10;

  const breakdownParams = {
    sessionId: selectedSession?.value ?? '',
    fromDate: fromDate ? formatDateForAPI(fromDate) : '',
    toDate: toDate ? formatDateForAPI(toDate) : '',
  };

  // API hooks for fetching session billing breakdown (disabled by default)
  const { refetch: refetchV1, isFetching: isFetchingV1 } =
    useGetSessionBillingBreakdown(breakdownParams);
  const { refetch: refetchV2, isFetching: isFetchingV2 } =
    useGetSessionBillingBreakdownV2(breakdownParams);
  const isFetching = isV2HourlyTuition ? isFetchingV2 : isFetchingV1;

  // Get draft data from React Query cache
  const { data: draftData } = useQuery<{
    sessionId: string;
    fromDate: Date;
    toDate: Date;
    autoFetch: boolean;
  }>({
    queryKey: ['draft-continue'],
    enabled: false, // Don't fetch, just read from cache
  });

  // Fetch sessions to get the proper session data
  const { data: sessionsData } = useGetAllSessions({
    page: 1,
    limit: 100, // Get more sessions to ensure we find the one we need
  });

  // Handle draft data from navigation
  useEffect(() => {
    if (draftData && sessionsData) {
      const {
        sessionId,
        fromDate: draftFromDate,
        toDate: draftToDate,
        autoFetch,
      } = draftData;

      // Find the session in the fetched sessions
      const foundSession = sessionsData.sessions.find(
        (s) => s._id === sessionId,
      );

      if (foundSession) {
        setSelectedSession({
          label: foundSession.name, // Use the actual session name
          value: foundSession._id,
        });
      } else if (sessionId) {
        // Fallback to ID if session not found
        setSelectedSession({
          label: sessionId, // Just show the ID
          value: sessionId,
        });
      }

      // Set the dates
      setFromDate(draftFromDate);
      setToDate(draftToDate);

      // Auto-fetch if requested (wait for V2 mode so we pick the right API)
      if (autoFetch && sessionId && !isV2ModeLoading) {
        // Trigger refetch after a short delay to ensure state is set
        setTimeout(async () => {
          if (isV2HourlyTuition) {
            await refetchV2();
          } else {
            await refetchV1();
          }
        }, 100);
      }
    }
  }, [
    draftData,
    sessionsData,
    isV2HourlyTuition,
    isV2ModeLoading,
    refetchV1,
    refetchV2,
  ]);

  // API hook for posting tuition
  const postTuitionMutation = usePostTuition();

  // API hooks for draft functionality
  const saveDraftMutation = useSaveDraft();
  const updateDraftMutation = useUpdateDraft();

  const applyDiscount = (
    base: number,
    discountType: HourlyTuitionDiscountType,
    discountValue: number,
    multiStudentDiscount = 0,
  ): number => {
    const afterMulti = Math.max(0, base - multiStudentDiscount);
    if (discountType === HourlyTuitionDiscountType.PERCENTAGE) {
      return afterMulti - (afterMulti * discountValue) / 100;
    }
    return Math.max(0, afterMulti - discountValue);
  };

  const staffDiscountAppliedAmount = (
    base: number,
    discountType: HourlyTuitionDiscountType,
    discountValue: number,
    multiStudentDiscount = 0,
  ): number => {
    const afterMulti = Math.max(0, base - multiStudentDiscount);
    if (discountType === HourlyTuitionDiscountType.PERCENTAGE) {
      return (afterMulti * discountValue) / 100;
    }
    return Math.min(discountValue, afterMulti);
  };

  const fetchSessions = async () => {
    if (!selectedSession?.value || !fromDate || !toDate) {
      return;
    }

    if (isV2ModeLoading) {
      toast.error('Loading V2 mode status…');
      return;
    }

    try {
      const result = isV2HourlyTuition ? await refetchV2() : await refetchV1();

      if (!result.data?.success && result.data?.data.length === 0) {
        toast.error(
          result.data.warningMessage ??
            'No hourly enrollments found for this session and date range',
        );
        setStudentGroups([]);
        return;
      }

      if (result.data?.warningMessage) {
        setWarningMessage(result.data.warningMessage);
        setProcessingHistory(result.data.processingHistory ?? null);
        setShowWarningDialog(true);
        setPendingAction(() => () => {
          processBillingData(result.data);
        });
        return;
      }

      if (result.data) {
        processBillingData(result.data);
      }
    } catch (error) {
      console.error('Error fetching session billing data:', error);
      toast.error('Failed to fetch session billing breakdown');
    }
  };

  const handleWarningCancel = () => {
    // Clear the data and close the dialog
    setStudentGroups([]);
    setShowWarningDialog(false);
    setWarningMessage(null);
    setProcessingHistory(null);
    setPendingAction(null);
  };

  const handleWarningProceed = () => {
    // Execute the pending action (process the data)
    if (pendingAction) {
      pendingAction();
    }
    setShowWarningDialog(false);
    setWarningMessage(null);
    setProcessingHistory(null);
    setPendingAction(null);
  };

  const processBillingData = (data: SessionBillingBreakdownResponse) => {
    // Create a map of previously processed students from processing history
    const previouslyProcessedStudents = new Map<
      string,
      { invoiceId: string | null; invoiceStatus: string | null }
    >();

    if (data.processingHistory && data.processingHistory.length > 0) {
      data.processingHistory.forEach((record: ProcessingRecord) => {
        record.students.forEach((student: StudentInfo) => {
          if (!previouslyProcessedStudents.has(student.studentId)) {
            previouslyProcessedStudents.set(student.studentId, {
              invoiceId: student.invoiceId,
              invoiceStatus: student.invoiceStatus,
            });
          }
        });
      });
    }

    const grouped = data.data.map(
      (studentData: StudentBillingData): StudentGroup => {
        // Group sessions by class and combine days; keep enrollment ref for per-enrollment display
        const sessionsByClass = new Map<
          string,
          {
            days: Array<string>;
            class_type: string;
            totalMinutes: number;
            totalMinutesFromEnrollment: number;
            enrollmentId: string;
          }
        >();

        studentData.enrollments.forEach((enrollment: Enrollment) => {
          enrollment.sessionDetails.forEach((sessionDetail: SessionDetail) => {
            const key = `${enrollment.enrollmentId}-${sessionDetail.class}`;

            const existing = sessionsByClass.get(key);
            if (existing) {
              existing.days.push(sessionDetail.day);
              existing.totalMinutes += sessionDetail.minutes;
            } else {
              sessionsByClass.set(key, {
                days: [sessionDetail.day],
                class_type: sessionDetail.class,
                totalMinutes: sessionDetail.minutes,
                totalMinutesFromEnrollment: enrollment.totalMinutes,
                enrollmentId: enrollment.enrollmentId,
              });
            }
          });
        });

        // Convert to sessions array; attach per-enrollment display data so each row shows correct range/rate/discount
        const allSessions = Array.from(sessionsByClass.entries()).map(
          ([key, sessionGroup]) => {
            const enrollment = studentData.enrollments.find(
              (e: Enrollment) => e.enrollmentId === sessionGroup.enrollmentId,
            );
            return {
              id: `${studentData.studentId}-${key}`,
              enrollmentId: sessionGroup.enrollmentId,
              student: studentData.studentName,
              days: sessionGroup.days.join(', '),
              class_type: sessionGroup.class_type,
              duration_minutes: sessionGroup.totalMinutes,
              total_minutes: sessionGroup.totalMinutesFromEnrollment,
              applicableRange: enrollment?.applicableRange ?? null,
              totalTuition: enrollment?.totalTuition,
              discountApplied: enrollment?.discountApplied,
              weeklyBreakdown: enrollment?.weeklyBreakdown,
            };
          },
        );

        // Use the total minutes from the backend
        const totalMinutes = studentData.totalMinutes;
        const baseRate = studentData.totalTuition;

        // Use the discount values from the backend API response
        const discountType =
          studentData.discountType ?? HourlyTuitionDiscountType.FIXED;
        const multiStudentDiscount = studentData.multiStudentDiscount ?? 0;

        // Staff/manual override only. Never seed from discountApplied —
        // that field includes auto multi-student discount, and posting it as
        // staff makes BE apply MSD twice ($10 -> $20).
        let discountValue = studentData.discountValue ?? 0;
        if (
          isV2HourlyTuition &&
          discountType === HourlyTuitionDiscountType.FIXED &&
          multiStudentDiscount > 0 &&
          Math.abs(discountValue - multiStudentDiscount) < 0.005
        ) {
          discountValue = 0;
        }

        const discountApplied = studentData.discountApplied || 0;
        const finalAmount = studentData.finalAmount;

        // Fallback display from first enrollment (for single-enrollment students and legacy)
        const applicableRange = studentData.enrollments[0]?.applicableRange;
        const errorMessage = studentData.enrollments[0]?.errorMessage;
        const billingRuleName = studentData.enrollments[0]?.billingRuleName;
        const weeklyBreakdown = studentData.enrollments[0]?.weeklyBreakdown;

        // Resolve posted status from week-level API fields (overlap-aware), not fetch window
        const postingStatus =
          studentData.postingStatus ?? HourlyTuitionPostingStatus.NONE;
        const isFullyPosted = postingStatus === HourlyTuitionPostingStatus.FULL;
        const isPartiallyPosted =
          postingStatus === HourlyTuitionPostingStatus.PARTIAL;
        const processedInfo = previouslyProcessedStudents.get(
          studentData.studentId,
        );
        const failureStorageKey =
          selectedSession?.value && fromDate && toDate
            ? buildHourlyPostFailureStorageKey({
                sessionId: selectedSession.value,
                fromDate: formatDateForAPI(fromDate),
                toDate: formatDateForAPI(toDate),
              })
            : null;
        const storedFailures = failureStorageKey
          ? readHourlyPostFailures(failureStorageKey)
          : {};

        return {
          student: studentData.studentName,
          studentId: studentData.studentId,
          sessions: allSessions,
          total_minutes: totalMinutes,
          base_rate: baseRate,
          discount_type: discountType,
          discount_value: discountValue,
          discount_applied: discountApplied,
          multi_student_discount: multiStudentDiscount,
          final_amount: finalAmount,
          isExpanded: !isFullyPosted,
          isDone: false,
          isDraft: studentData.isDraft ?? false,
          draftId: studentData.draftId,
          isAlreadyProcessed: isFullyPosted,
          isPartiallyProcessed: isPartiallyPosted,
          invoiceId:
            studentData.postedInvoiceId ?? processedInfo?.invoiceId ?? null,
          invoiceStatus: resolveStudentInvoiceStatus(
            studentData,
            processedInfo,
          ),
          lastPostFailure: storedFailures[studentData.studentId] ?? null,
          applicableRange: applicableRange,
          errorMessage: errorMessage,
          billingRuleName: billingRuleName,
          weeklyBreakdown: weeklyBreakdown,
          enrollments: studentData.enrollments,
        };
      },
    );

    setStudentGroups(grouped);
  };

  const refreshBillingAfterPost = async (
    postResults: Array<{
      studentId: string;
      status: HourlyTuitionPostStudentStatus;
      error?: string | null;
      message?: string | null;
    }>,
  ) => {
    const failureByStudentId = new Map<string, string>();
    for (const row of postResults) {
      if (row.status !== HourlyTuitionPostStudentStatus.FAILED) continue;
      const reason = (
        row.error ??
        row.message ??
        'Previous post failed'
      ).trim();
      if (reason) failureByStudentId.set(row.studentId, reason);
    }

    if (
      selectedSession?.value &&
      fromDate &&
      toDate &&
      failureByStudentId.size > 0
    ) {
      const storageKey = buildHourlyPostFailureStorageKey({
        sessionId: selectedSession.value,
        fromDate: formatDateForAPI(fromDate),
        toDate: formatDateForAPI(toDate),
      });
      writeHourlyPostFailures(
        storageKey,
        mergeHourlyPostFailures(
          readHourlyPostFailures(storageKey),
          failureByStudentId,
        ),
      );
    }

    const result = isV2HourlyTuition ? await refetchV2() : await refetchV1();
    if (result.data) {
      processBillingData(result.data);
    }

    if (failureByStudentId.size === 0) return;

    setStudentGroups((current) =>
      current.map((group) => {
        const prior = failureByStudentId.get(group.studentId);
        if (!prior) return group;
        return {
          ...group,
          lastPostFailure: prior,
          isExpanded: true,
        };
      }),
    );
  };

  const query = searchQuery.toLowerCase();
  const filteredStudents = studentGroups.filter((group) => {
    const matchesSearch = group.student.toLowerCase().includes(query);
    const matchesChargeFilter =
      !showOnlyUncharged || !isStudentFullyCharged(group);

    return matchesSearch && matchesChargeFilter;
  });

  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);
  const startIndex = (currentPage - 1) * studentsPerPage;
  const paginatedStudents = filteredStudents.slice(
    startIndex,
    startIndex + studentsPerPage,
  );

  const updateBaseRate = (studentName: string, baseRate: number) => {
    setStudentGroups(
      studentGroups.map((group) => {
        if (group.student === studentName) {
          const staffApplied = staffDiscountAppliedAmount(
            baseRate,
            group.discount_type,
            group.discount_value,
            group.multi_student_discount,
          );
          const final_amount = applyDiscount(
            baseRate,
            group.discount_type,
            group.discount_value,
            group.multi_student_discount,
          );
          return {
            ...group,
            base_rate: baseRate,
            discount_applied: group.multi_student_discount + staffApplied,
            final_amount,
          };
        }
        return group;
      }),
    );
  };

  // Save draft for individual student
  const saveStudentDraft = async (studentId: string, studentName: string) => {
    if (!selectedSession?.value || !fromDate || !toDate) {
      toast.error('Please select session and date range first');
      return;
    }

    const student = studentGroups.find(
      (group) => group.studentId === studentId,
    );
    if (!student) {
      toast.error('Student not found');
      return;
    }

    try {
      const discountData = {
        discountType: student.discount_type,
        discount: student.discount_value,
      };

      if (student.isDraft && student.draftId) {
        // Update existing draft
        await updateDraftMutation.mutateAsync({
          draftId: student.draftId,
          data: discountData,
        });
        toast.success(`Draft updated for ${studentName}`);
      } else {
        // Create new draft
        const draftData = {
          sessionId: selectedSession.value,
          fromDate: formatDateForAPI(fromDate),
          toDate: formatDateForAPI(toDate),
          studentId: studentId,
          ...discountData,
        };

        const result = await saveDraftMutation.mutateAsync(draftData);

        // Check if the result has the expected structure
        if (!result.draftId) {
          console.error('Invalid save draft response:', result);
          toast.error('Failed to save draft - invalid response');
          return;
        }

        // Update local state to show draft badge and store draft ID
        setStudentGroups((prev) =>
          prev.map((group) =>
            group.studentId === studentId
              ? {
                  ...group,
                  isDraft: true,
                  draftId: result.draftId, // draftId is at the top level, not in data
                }
              : group,
          ),
        );

        toast.success(`Draft saved for ${studentName}`);
      }
    } catch (error) {
      console.error('Error saving/updating draft:', error);
      toast.error('Failed to save/update draft');
    }
  };

  const updateDiscountType = (
    studentName: string,
    discountType: HourlyTuitionDiscountType,
  ) => {
    setStudentGroups(
      studentGroups.map((group) => {
        if (group.student === studentName) {
          const staffApplied = staffDiscountAppliedAmount(
            group.base_rate,
            discountType,
            group.discount_value,
            group.multi_student_discount,
          );
          const final_amount = applyDiscount(
            group.base_rate,
            discountType,
            group.discount_value,
            group.multi_student_discount,
          );
          return {
            ...group,
            discount_type: discountType,
            discount_applied: group.multi_student_discount + staffApplied,
            final_amount,
          };
        }
        return group;
      }),
    );
  };

  const updateDiscountValue = (studentName: string, discountValue: number) => {
    setStudentGroups(
      studentGroups.map((group) => {
        if (group.student === studentName) {
          const staffApplied = staffDiscountAppliedAmount(
            group.base_rate,
            group.discount_type,
            discountValue,
            group.multi_student_discount,
          );
          const final_amount = applyDiscount(
            group.base_rate,
            group.discount_type,
            discountValue,
            group.multi_student_discount,
          );
          return {
            ...group,
            discount_value: discountValue,
            discount_applied: group.multi_student_discount + staffApplied,
            final_amount,
          };
        }
        return group;
      }),
    );
  };

  const toggleStudentExpansion = (studentName: string) => {
    setStudentGroups(
      studentGroups.map((group) =>
        group.student === studentName
          ? { ...group, isExpanded: !group.isExpanded }
          : group,
      ),
    );
  };

  const toggleStudentDone = (studentName: string) => {
    setStudentGroups(
      studentGroups.map((group) => {
        if (
          group.student !== studentName ||
          !isStudentSelectableForPosting(group)
        ) {
          return group;
        }
        return {
          ...group,
          isDone: !group.isDone,
          isExpanded: !group.isDone ? false : group.isExpanded,
        };
      }),
    );
  };

  const selectablePaginatedStudents = paginatedStudents.filter(
    isStudentSelectableForPosting,
  );

  // Scope-wide selectable students: respects search + "show only uncharged" filters,
  // but spans all pages (not just the visible one).
  const selectableInScope = filteredStudents.filter(
    isStudentSelectableForPosting,
  );

  const hasAnyCurrentPageSelected = selectablePaginatedStudents.some(
    (group) => group.isDone,
  );
  const totalSelectedCount = studentGroups.filter(
    (group) => group.isDone && isStudentSelectableForPosting(group),
  ).length;
  // Prefer "Deselect All" whenever any selectable row is checked — including ones
  // hidden by search/"show only uncharged" — so the counter, button label, and
  // post payload stay consistent.
  const hasAnySelected = totalSelectedCount > 0;

  // Toggle select all selectable students on current page
  const toggleSelectCurrentPage = () => {
    const shouldSelect = !hasAnyCurrentPageSelected;
    const currentPageStudentIds = new Set(
      selectablePaginatedStudents.map((group) => group.studentId),
    );

    setStudentGroups(
      studentGroups.map((group) => {
        if (!currentPageStudentIds.has(group.studentId)) {
          return group;
        }
        return {
          ...group,
          isDone: shouldSelect,
          isExpanded: shouldSelect ? false : group.isExpanded,
        };
      }),
    );
  };

  // Select All: active scope only (search / uncharged filter).
  // Deselect All: clear every selected selectable student, including ones hidden
  // by the current filters, so they cannot linger and still post.
  const toggleSelectAllInScope = () => {
    if (hasAnySelected) {
      setStudentGroups(
        studentGroups.map((group) => {
          if (!group.isDone || !isStudentSelectableForPosting(group)) {
            return group;
          }
          return {
            ...group,
            isDone: false,
          };
        }),
      );
      return;
    }

    const scopeStudentIds = new Set(
      selectableInScope.map((group) => group.studentId),
    );

    setStudentGroups(
      studentGroups.map((group) => {
        if (!scopeStudentIds.has(group.studentId)) {
          return group;
        }
        return {
          ...group,
          isDone: true,
          isExpanded: false,
        };
      }),
    );
  };

  const postToAccounts = () => {
    // Filter only selectable students marked as done
    const completedStudents = studentGroups.filter(
      (group) => group.isDone && isStudentSelectableForPosting(group),
    );

    if (completedStudents.length === 0) {
      toast.error(
        'Please mark at least one student as done before posting to accounts.',
      );
      return;
    }

    if (!selectedSession?.value || !fromDate || !toDate) {
      toast.error('Please ensure session and date range are selected.');
      return;
    }

    // Show summary modal
    setShowSummaryModal(true);
  };

  const confirmPostToAccounts = async () => {
    if (isV2ModeLoading) {
      toast.error('Loading V2 mode status…');
      return;
    }
    if (!selectedSession?.value || !fromDate || !toDate) {
      toast.error('Please ensure session and date range are selected.');
      return;
    }

    const completedStudents = studentGroups.filter(
      (group) => group.isDone && isStudentSelectableForPosting(group),
    );

    try {
      const useInvoiceOnly =
        isV2HourlyTuition && v2PostMode === 'invoiceOnly';
      const useScheduledPayment = isV2HourlyTuition
        ? v2PostMode === 'schedule'
        : isScheduledPayment;

      const postData: {
        sessionId: string;
        fromDate: string;
        toDate: string;
        students: Array<{
          studentId: string;
          discountType: HourlyTuitionDiscountType;
          discount: number;
        }>;
        isScheduledPayment: boolean;
        invoiceOnly?: boolean;
        scheduledDate: string | undefined;
        description: string;
      } = {
        sessionId: selectedSession.value,
        fromDate: formatDateForAPI(fromDate),
        toDate: formatDateForAPI(toDate),
        students: completedStudents.map((group) => ({
          studentId: group.studentId,
          discountType: group.discount_type,
          // V2: BE already applies multi_student_discount. If the staff field
          // still equals that auto amount (legacy echo / race), send 0.
          discount: resolveStaffDiscountForPost(
            group.discount_value,
            group.multi_student_discount,
            isV2HourlyTuition,
            group.discount_type,
          ),
        })),
        isScheduledPayment: useScheduledPayment,
        ...(useInvoiceOnly ? { invoiceOnly: true } : {}),
        scheduledDate:
          useScheduledPayment && scheduledDate
            ? formatDateForAPI(scheduledDate)
            : undefined,
        description: confirmationDescription,
      };

      const result = await postTuitionMutation.mutateAsync({
        data: postData,
        isV2: isV2HourlyTuition,
      });

      const successCount = result.results.filter(
        (r) => r.status === HourlyTuitionPostStudentStatus.SUCCESS,
      ).length;
      const failedCount = result.results.filter(
        (r) => r.status === HourlyTuitionPostStudentStatus.FAILED,
      ).length;
      const skippedCount = result.results.filter((r) =>
        isHourlyTuitionPostSkipStatus(r.status),
      ).length;

      if (result.success) {
        if (failedCount === 0) {
          toast.success(`${successCount} success / 0 failed`);
        } else {
          toast.warning(
            `${successCount} success / ${failedCount} failed. please repost tuition to see students that failed`,
          );
        }

        setShowSummaryModal(false);
        setIsScheduledPayment(false);
        setV2PostMode('invoiceOnly');
        setScheduledDate(undefined);
        setConfirmationDescription('');
        await refreshBillingAfterPost(result.results);
      } else if (result.results.length > 0) {
        if (failedCount > 0) {
          toast.error(
            `${successCount} success / ${failedCount} failed. please repost tuition to see students that failed`,
          );
        } else if (skippedCount > 0) {
          toast.warning(
            result.message ?? `${successCount} success / ${failedCount} failed`,
          );
        } else {
          toast.error(result.message ?? 'Failed to post tuition.');
        }
        await refreshBillingAfterPost(result.results);
      } else {
        toast.error(result.message ?? 'Failed to post tuition.');
      }
    } catch (error) {
      console.error('Error posting tuition:', error);
      toast.error('Failed to post tuition. Please try again.');
    }
  };

  const exportCSV = () => {
    const headers = [
      'Student Name',
      'Total Minutes',
      'Base Rate',
      'Discount Type',
      'Discount Value',
      'Discount Applied',
      'Final Amount',
      'Sessions Detail',
    ];
    const csvContent = [
      headers.join(','),
      ...studentGroups.map((group) =>
        [
          group.student,
          group.total_minutes,
          group.base_rate,
          group.discount_type,
          group.discount_value,
          group.discount_applied,
          group.final_amount,
          group.sessions
            .map((s) => `${s.days}:${s.class_type}:${s.duration_minutes}min`)
            .join(';'),
        ].join(','),
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tuition-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const totalAmount = studentGroups.reduce(
    (sum, group) => sum + group.final_amount,
    0,
  );

  return (
    <>
      <div className='mb-4 border-b pb-4'>
        <Link
          className='flex w-fit items-center gap-2'
          to='/payment/hourly-tuition'
        >
          <CircleArrowLeft className='size-6 text-custom-secondary-1' />
          <h2 className='text-2xl font-semibold text-custom-secondary-1'>
            Tuition Review
          </h2>
        </Link>
      </div>

      <div className='mx-auto max-w-6xl space-y-6'>
        <div className='text-center'>
          <p className='text-[#6B7280]'>
            Review all class sessions and post charges to student accounts.
          </p>
        </div>

        <Card>
          <CardContent className='space-y-4 p-6'>
            <div className='space-y-1.5'>
              <label
                htmlFor='session-selector'
                className='text-sm font-medium text-slate-700'
              >
                Session
              </label>
              <SessionSelector
                session={selectedSession}
                onChange={setSelectedSession}
                disabled={isFetching}
                includeArchivedSessions
                searchLimit={100}
              />
            </div>
            {selectedSession && (
              <div className='flex flex-wrap items-end gap-4'>
                <div>
                  <label className='mb-2 block text-sm font-medium text-[#111827]'>
                    From Date
                  </label>
                  <DateTimePicker
                    className='w-48'
                    hourCycle={12}
                    granularity='day'
                    displayFormat={{ hour12: 'PPP' }}
                    value={fromDate}
                    onChange={(date) => {
                      setFromDate(date);
                    }}
                    disabled={isFetching}
                  />
                </div>
                <div>
                  <label className='mb-2 block text-sm font-medium text-[#111827]'>
                    To Date
                  </label>
                  <DateTimePicker
                    className='w-48'
                    hourCycle={12}
                    granularity='day'
                    displayFormat={{ hour12: 'PPP' }}
                    value={toDate}
                    onChange={(date) => {
                      setToDate(date);
                    }}
                    disabled={isFetching}
                  />
                </div>
                <Button
                  onClick={fetchSessions}
                  disabled={
                    isFetching ||
                    isV2ModeLoading ||
                    !selectedSession.value ||
                    !fromDate ||
                    !toDate
                  }
                  variant='secondary'
                >
                  {isFetching ? (
                    <Loader2 className='animate-spin' />
                  ) : (
                    <Search />
                  )}
                  Fetch Students
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {studentGroups.length > 0 && (
          <div className='space-y-4'>
            <Card>
              <CardContent className='p-4'>
                <div className='flex flex-wrap items-center gap-4'>
                  <Input
                    type='text'
                    placeholder='Search student name...'
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className='max-w-md'
                  />
                  <div className='flex items-center gap-2'>
                    <Switch
                      id='show-only-uncharged'
                      checked={showOnlyUncharged}
                      onCheckedChange={(checked) => {
                        setShowOnlyUncharged(checked);
                        setCurrentPage(1);
                      }}
                    />
                    <Label
                      htmlFor='show-only-uncharged'
                      className='cursor-pointer text-sm font-medium text-[#374151]'
                    >
                      Show only uncharged
                    </Label>
                  </div>
                  <Button
                    onClick={toggleSelectCurrentPage}
                    disabled={selectablePaginatedStudents.length === 0}
                    variant='outline'
                    className='border-[#003366] bg-transparent text-[#003366] hover:bg-[#F0F4F8]'
                  >
                    {hasAnyCurrentPageSelected
                      ? 'Deselect Current'
                      : 'Select Current'}
                  </Button>
                  <Button
                    onClick={toggleSelectAllInScope}
                    disabled={selectableInScope.length === 0 && !hasAnySelected}
                    variant='outline'
                    className='border-[#003366] bg-transparent text-[#003366] hover:bg-[#F0F4F8]'
                  >
                    {hasAnySelected ? 'Deselect All' : 'Select All'}
                  </Button>
                  <span className='text-sm font-medium text-[#374151]'>
                    {totalSelectedCount} selected
                  </span>
                </div>
              </CardContent>
            </Card>

            {paginatedStudents.map((group) => {
              const postedOrChargedLabel = resolvePostedOrChargedLabel(group);

              return (
                <Card
                  key={group.student}
                  className={cn(
                    group.isDone && 'opacity-60',
                    postedOrChargedLabel === 'Charged' &&
                      'border-orange-200 bg-orange-50/50 opacity-80',
                    postedOrChargedLabel === 'Posted' &&
                      'border-sky-200 bg-sky-50/40 opacity-90',
                    (postedOrChargedLabel === 'Partially Charged' ||
                      postedOrChargedLabel === 'Partially Posted') &&
                      'border-amber-200 bg-amber-50/30',
                  )}
                >
                  <CardHeader
                    className='cursor-pointer transition-colors hover:bg-[#F3F4F6]'
                    onClick={() => {
                      toggleStudentExpansion(group.student);
                    }}
                  >
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center gap-3'>
                        <input
                          type='checkbox'
                          className='mr-2 disabled:cursor-not-allowed disabled:opacity-50'
                          checked={group.isDone}
                          disabled={group.isAlreadyProcessed}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleStudentDone(group.student);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        />
                        {group.isDone ? (
                          <div className='flex items-center gap-2'>
                            <Check className='size-5 text-[#10B981]' />
                            <span className='text-sm font-medium text-[#10B981]'>
                              To be Processed
                            </span>
                          </div>
                        ) : (
                          <>
                            {group.isExpanded ? (
                              <ChevronDown className='size-5 text-[#6B7280]' />
                            ) : (
                              <ChevronRight className='size-5 text-[#6B7280]' />
                            )}
                          </>
                        )}
                        {group.isDraft && (
                          <span className='ml-2 rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-600'>
                            DRAFT
                          </span>
                        )}

                        <div>
                          <CardTitle className='text-lg font-bold text-[#111827]'>
                            {group.student}
                          </CardTitle>
                          <p className='text-sm text-[#6B7280]'>
                            {group.sessions.length} classes ·{' '}
                            {group.total_minutes} min
                          </p>
                        </div>
                      </div>
                      <div className='flex flex-col items-end gap-1'>
                        <div className='text-lg font-bold text-[#FDC500]'>
                          {currencySymbol}
                          {group.final_amount.toFixed(2)}
                        </div>
                        <div className='flex flex-wrap items-center justify-end gap-2'>
                          {getStudentChargeStatusBadge(group)}
                          {group.isAlreadyProcessed ||
                          group.isPartiallyProcessed
                            ? getInvoiceStatusBadge(
                                group.invoiceStatus ?? null,
                                {
                                  showPrefix: true,
                                },
                              )
                            : null}
                        </div>
                      </div>
                    </div>
                  </CardHeader>

                  {group.isExpanded && !group.isDone && (
                    <CardContent>
                      <div className='mb-6'>
                        <h4 className='mb-3 text-sm font-medium text-[#111827]'>
                          Session Details
                        </h4>
                        <TuitionSessionsTable
                          group={group}
                          currencySymbol={currencySymbol}
                        />
                      </div>

                      {group.errorMessage && (
                        <div className='mb-6'>
                          <div className='rounded-lg border border-[#FECACA] bg-[#FEF2F2] p-4'>
                            <div className='flex items-start gap-3'>
                              <div className='shrink-0'>
                                <svg
                                  className='size-5 text-[#DC2626]'
                                  viewBox='0 0 20 20'
                                  fill='currentColor'
                                >
                                  <path
                                    fillRule='evenodd'
                                    d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z'
                                    clipRule='evenodd'
                                  />
                                </svg>
                              </div>
                              <div>
                                <h4 className='text-sm font-medium text-[#DC2626]'>
                                  Billing Error
                                </h4>
                                <p className='mt-1 text-sm text-[#DC2626]'>
                                  {group.errorMessage}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Legacy Billing Range Section (for backward compatibility) */}
                      {group.applicableRange && !group.weeklyBreakdown && (
                        <div className='mb-6'>
                          <h4 className='mb-3 text-sm font-medium text-[#111827]'>
                            Billing Range Applied
                          </h4>
                          <div className='rounded-lg bg-[#FEF3C7] p-4'>
                            <div className='flex flex-wrap gap-4 text-sm'>
                              <div>
                                <span className='font-medium text-[#92400E]'>
                                  Range:
                                </span>
                                <span className='ml-1 text-[#92400E]'>
                                  {group.applicableRange.minMinute} -{' '}
                                  {group.applicableRange.maxMinute} minutes
                                </span>
                              </div>
                              <div>
                                <span className='font-medium text-[#92400E]'>
                                  Flat Rate:
                                </span>
                                <span className='ml-1 text-[#92400E]'>
                                  {currencySymbol}
                                  {formatHourlyTuitionAmount(
                                    group.applicableRange.flatRate,
                                  )}
                                </span>
                              </div>
                              <div>
                                <span className='font-medium text-[#92400E]'>
                                  Student Total:
                                </span>
                                <span className='ml-1 text-[#92400E]'>
                                  {group.total_minutes} minutes
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className='grid grid-cols-1 gap-4 rounded-lg bg-[#F9FAFB] p-4 md:grid-cols-5'>
                        <div>
                          <label className='mb-2 block text-sm font-medium text-[#111827]'>
                            Total Minutes: {group.total_minutes}
                          </label>
                        </div>
                        <div>
                          <label className='mb-2 block text-sm font-medium text-[#111827]'>
                            Base Rate
                          </label>
                          <Input
                            type='number'
                            value={group.base_rate}
                            onChange={(e) => {
                              updateBaseRate(
                                group.student,
                                Number(e.target.value),
                              );
                            }}
                            disabled={true}
                            className='w-full'
                          />
                        </div>
                        <div>
                          <label className='mb-2 block text-sm font-medium text-[#111827]'>
                            Discount
                          </label>
                          <div className='flex items-center gap-2'>
                            <div className='flex items-center rounded-md border border-input bg-background'>
                              <Select
                                value={group.discount_type}
                                onValueChange={(
                                  value: HourlyTuitionDiscountType,
                                ) => {
                                  updateDiscountType(group.student, value);
                                }}
                              >
                                <SelectTrigger className='w-16 border-0 bg-transparent shadow-none focus:ring-0'>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem
                                    value={HourlyTuitionDiscountType.PERCENTAGE}
                                  >
                                    %
                                  </SelectItem>
                                  <SelectItem
                                    value={HourlyTuitionDiscountType.FIXED}
                                  >
                                    {currencySymbol || '$'}
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <div className='h-6 w-px bg-border' />
                              <Input
                                type='number'
                                value={group.discount_value || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Remove leading zeros and convert to number
                                  const numericValue =
                                    value === ''
                                      ? 0
                                      : Number(value.replace(/^0+/, '') || '0');
                                  updateDiscountValue(
                                    group.student,
                                    numericValue,
                                  );
                                }}
                                className='border-0 bg-transparent shadow-none focus-visible:ring-0'
                                min='0'
                                placeholder='0'
                              />
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className='mb-2 block text-sm font-medium text-[#111827]'>
                            Final:{' '}
                            <span className='font-bold text-[#FDC500]'>
                              {currencySymbol}
                              {group.final_amount.toFixed(2)}
                            </span>
                          </label>
                        </div>
                      </div>

                      {/* Save as Draft button */}
                      <div className='mt-4 flex justify-end'>
                        <Button
                          onClick={() =>
                            saveStudentDraft(group.studentId, group.student)
                          }
                          disabled={
                            saveDraftMutation.isPending ||
                            updateDraftMutation.isPending
                          }
                          variant='outline'
                          className='border-blue-500 bg-transparent text-blue-500 hover:bg-blue-50'
                        >
                          {saveDraftMutation.isPending ||
                          updateDraftMutation.isPending
                            ? 'Saving...'
                            : group.isDraft
                              ? 'Update Draft'
                              : 'Save as Draft'}
                        </Button>
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}

            {totalPages > 1 && (
              <Card>
                <CardContent className='p-4'>
                  <TruncatedPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    totalItems={filteredStudents.length}
                    itemLabel='students'
                  />
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent className='p-6'>
                <div className='flex items-center justify-between'>
                  <div className='text-2xl font-bold text-[#111827]'>
                    Grand Total:{' '}
                    <span className='text-[#FDC500]'>
                      {currencySymbol}
                      {totalAmount.toFixed(2)}
                    </span>
                  </div>
                  <div className='flex gap-3'>
                    <Button
                      onClick={exportCSV}
                      variant='outline'
                      className='border-[#003366] bg-transparent text-[#003366] hover:bg-[#F0F4F8]'
                    >
                      <Download className='mr-2 size-4' />
                      Export CSV
                    </Button>
                    <Button
                      onClick={postToAccounts}
                      disabled={
                        postTuitionMutation.isPending ||
                        !studentGroups.some(
                          (g) => g.isDone && isStudentSelectableForPosting(g),
                        )
                      }
                      className='bg-[#FDC500] text-[#1F1F1F] hover:bg-[#F59E0B] disabled:opacity-50'
                    >
                      <Send className='mr-2 size-4' />
                      {postTuitionMutation.isPending
                        ? 'Processing...'
                        : isV2HourlyTuition
                          ? 'Post invoices only'
                          : 'Post to Accounts'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Summary Modal */}
        <Dialog open={showSummaryModal} onOpenChange={setShowSummaryModal}>
          <DialogContent className='max-w-2xl'>
            <DialogHeader>
              <DialogTitle>
                {isV2HourlyTuition
                  ? 'Confirm invoice posting'
                  : 'Confirm Tuition Posting'}
              </DialogTitle>
              <DialogDescription>
                {isV2HourlyTuition && v2PostMode === 'invoiceOnly'
                  ? 'These balances will become visible to families immediately. No payment methods will be charged and no payments will be scheduled.'
                  : 'Review the charges that will be posted to accounts.'}
              </DialogDescription>
            </DialogHeader>

            <div className='space-y-4'>
              {/* Summary Table */}
              <div className='rounded-lg border'>
                <div className='bg-gray-50 px-4 py-3'>
                  <h3 className='font-medium'>
                    {isV2HourlyTuition && v2PostMode === 'invoiceOnly'
                      ? 'Invoices to post:'
                      : 'Students to be charged:'}
                  </h3>
                </div>
                <div className='max-h-60 overflow-y-auto'>
                  {studentGroups
                    .filter((group) => group.isDone)
                    .map((group) => (
                      <div
                        key={group.studentId}
                        className='flex items-center justify-between border-b px-4 py-3 last:border-b-0'
                      >
                        <div>
                          <p className='font-medium'>{group.student}</p>
                          <p className='text-sm text-gray-500'>
                            {group.sessions.map((s) => s.days).join(', ')} -{' '}
                            {group.total_minutes} min
                          </p>
                        </div>
                        <div className='text-right'>
                          <p className='font-medium'>
                            {currencySymbol}
                            {group.final_amount.toFixed(2)}
                          </p>
                          <p className='text-sm text-gray-500'>
                            {group.discount_type ===
                            HourlyTuitionDiscountType.PERCENTAGE
                              ? `${group.discount_value}%`
                              : `${currencySymbol}${group.discount_value}`}{' '}
                            off ({currencySymbol}
                            {group.discount_applied.toFixed(2)})
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
                <div className='bg-gray-50 px-4 py-3'>
                  <div className='flex justify-between font-medium'>
                    <span>Total Amount:</span>
                    <span>
                      {currencySymbol}
                      {studentGroups
                        .filter((group) => group.isDone)
                        .reduce((sum, group) => sum + group.final_amount, 0)
                        .toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* V2 post mode / V1 schedule toggle */}
              <div className='space-y-4'>
                {isV2HourlyTuition ? (
                  <div className='space-y-3 rounded-lg border p-4'>
                    <div className='text-base font-medium'>Posting action</div>
                    <div className='space-y-2'>
                      {(
                        [
                          {
                            value: 'invoiceOnly' as const,
                            label: 'Post invoices only',
                            hint: 'Create balances with no charge',
                          },
                          {
                            value: 'chargeNow' as const,
                            label: 'Charge now',
                            hint: 'Create invoices and collect payment immediately',
                          },
                          {
                            value: 'schedule' as const,
                            label: 'Schedule payment',
                            hint: 'Create invoices and collect on a future date',
                          },
                        ] as const
                      ).map((option) => (
                        <label
                          key={option.value}
                          className='flex cursor-pointer items-start gap-3 rounded-md border p-3 has-[:checked]:border-[#003366] has-[:checked]:bg-[#F0F4F8]'
                        >
                          <input
                            type='radio'
                            name='v2-hourly-post-mode'
                            className='mt-1'
                            checked={v2PostMode === option.value}
                            onChange={() => {
                              setV2PostMode(option.value);
                              if (option.value !== 'schedule') {
                                setScheduledDate(undefined);
                              }
                            }}
                          />
                          <span>
                            <span className='block font-medium'>
                              {option.label}
                            </span>
                            <span className='block text-sm text-muted-foreground'>
                              {option.hint}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                    {v2PostMode === 'schedule' && (
                      <div className='space-y-2 pt-1'>
                        <label className='text-sm font-medium'>
                          Scheduled Date
                        </label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant='outline'
                              className={cn(
                                'w-full justify-start text-left font-normal',
                                !scheduledDate && 'text-muted-foreground',
                              )}
                            >
                              <CalendarIcon className='mr-2 size-4' />
                              {scheduledDate ? (
                                scheduledDate.toLocaleDateString()
                              ) : (
                                <span>Pick a date</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            collisionPadding={12}
                            className='w-auto max-w-[calc(100vw-1.5rem)] p-0'
                          >
                            <Calendar
                              mode='single'
                              selected={scheduledDate}
                              onSelect={setScheduledDate}
                              disabled={(date) => date < new Date()}
                              autoFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className='flex items-center justify-between rounded-lg border p-4'>
                      <div className='space-y-0.5'>
                        <div className='text-base font-medium'>
                          Schedule Payment
                        </div>
                        <div className='text-sm text-muted-foreground'>
                          Enable to schedule this payment for a future date
                        </div>
                      </div>
                      <Switch
                        checked={isScheduledPayment}
                        onCheckedChange={setIsScheduledPayment}
                      />
                    </div>

                    {isScheduledPayment && (
                      <div className='space-y-2'>
                        <label className='text-sm font-medium'>
                          Scheduled Date
                        </label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant='outline'
                              className={cn(
                                'w-full justify-start text-left font-normal',
                                !scheduledDate && 'text-muted-foreground',
                              )}
                            >
                              <CalendarIcon className='mr-2 size-4' />
                              {scheduledDate ? (
                                scheduledDate.toLocaleDateString()
                              ) : (
                                <span>Pick a date</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            collisionPadding={12}
                            className='w-auto max-w-[calc(100vw-1.5rem)] p-0'
                          >
                            <Calendar
                              mode='single'
                              selected={scheduledDate}
                              onSelect={setScheduledDate}
                              disabled={(date) => date < new Date()}
                              autoFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Description Section */}
              <div className='space-y-2'>
                <label className='text-sm font-medium'>
                  Description <span className='text-red-500'>*</span>
                </label>
                <Input
                  value={confirmationDescription}
                  onChange={(e) => {
                    setConfirmationDescription(e.target.value);
                  }}
                  placeholder='Enter description for this tuition posting...'
                  className='w-full'
                  required
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => {
                  setShowSummaryModal(false);
                  setConfirmationDescription('');
                  setIsScheduledPayment(false);
                  setV2PostMode('invoiceOnly');
                  setScheduledDate(undefined);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmPostToAccounts}
                disabled={
                  postTuitionMutation.isPending ||
                  (isV2HourlyTuition
                    ? v2PostMode === 'schedule' && !scheduledDate
                    : isScheduledPayment && !scheduledDate) ||
                  !confirmationDescription.trim()
                }
                className='bg-[#FDC500] text-[#1F1F1F] hover:bg-[#F59E0B]'
              >
                {postTuitionMutation.isPending
                  ? 'Processing...'
                  : isV2HourlyTuition && v2PostMode === 'invoiceOnly'
                    ? 'Post invoices only'
                    : 'Confirm & Post'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Warning Dialog for Already Processed */}
        <WarningDialog
          open={showWarningDialog}
          onOpenChange={setShowWarningDialog}
          message={warningMessage ?? ''}
          onConfirm={handleWarningProceed}
          onCancel={handleWarningCancel}
          confirmText='Proceed'
          cancelText='Cancel'
          processingHistory={processingHistory}
        />
      </div>
    </>
  );
};
